import React, { useState, useEffect, useRef } from 'react';
import './TeacherDashboard.css';
import ViewLogbooks from './ViewLogbooks';
import ManageTeacherAccount from './ManageTeacherAccount';
import ChecklistGrading from './ChecklistGrading';
import AssessmentGrading from './AssessmentGrading';
import ActivityTracker from './ActivityTracker';
import MonitorDepartment from './MonitorDepartment';
import CertificateGeneration from './CertificateGeneration';
import ClassicDRPMonitor from './ClassicDRPMonitor';
import InternalTests from './InternalTests';
import AppDownloadBanner from '../AppDownloadBanner/AppDownloadBanner';
import DemoVideoPlayer from '../DemoVideoPlayer/DemoVideoPlayer';
import LeaveRequests from './LeaveRequests';
import MyStudents from './MyStudents';
import StudentAssignmentHistory from './StudentAssignmentHistory';
import TeacherDissertation from './TeacherDissertation';
import AdditionalInfo from './AdditionalInfo';
import collegeConfig from '../../config/collegeConfig';
import { 
  subscribeToPushNotifications, 
  checkSubscriptionStatus,
} from '../../utils/pushNotificationHelper';

import {
  FaUserGraduate,
  FaClipboardCheck,
  FaBookMedical,
  FaCheckCircle,
  FaTachometerAlt,
  FaBars,
  FaTimes,
  FaSignOutAlt,
  FaUser,
  FaBell,
  FaUserCog,
  FaExclamationCircle,
  FaArrowRight,
  FaClipboardList,
  FaUsers,
  FaSearch,
  FaSave,
  FaChartBar,
  FaBuilding,
  FaFileAlt,
  FaCalendarCheck,
  FaAward,
  FaHospital,
  FaPaperPlane,
  FaGraduationCap,
  FaInfoCircle,
  FaHistory
} from 'react-icons/fa';

const TeacherDashboard = ({ onNavigate, programSwitcher }) => {
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const profileMenuRef = useRef(null);
  const [sidebar1Open, setsidebar1Open] = useState(true);
  const [activeMenu, setActiveMenu] = useState('dashboard');
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [user, setUser] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [monthlyVerificationStatus, setMonthlyVerificationStatus] = useState({
    hasVerifiedThisMonth: false,
    lastVerificationDate: null,
    pendingCount: 0,
    loading: true
  });
  const [studentsWithoutSubmission, setStudentsWithoutSubmission] = useState({
    students: [],
    loading: true,
    totalStudents: 0
  });
  
  // Course and data state
  const [assignedCourse, setAssignedCourse] = useState(null);
  const [dashboardStats, setDashboardStats] = useState({
    totalStudents: 0,
    submittedLogbooks: 0,
    verifiedLogbooks: 0,
    pendingVerification: 0
  });
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Notify students state
  const [sendingStudentNotif, setSendingStudentNotif] = useState(false);
  const [studentNotifResult, setStudentNotifResult] = useState(null);
  const [notifyingStudent, setNotifyingStudent] = useState(null);
  const [studentNotifResults, setStudentNotifResults] = useState({});

  // My Students state
  const [allStudents, setAllStudents] = useState([]);
  const [myStudentIds, setMyStudentIds] = useState([]);
  const [myStudentsSearch, setMyStudentsSearch] = useState('');
  const [myStudentsLoading, setMyStudentsLoading] = useState(true);
  const [myStudentsSaveSuccess, setMyStudentsSaveSuccess] = useState(false);

  // Animated counter for stats
  const [animatedStats, setAnimatedStats] = useState({
    students: 0,
    submitted: 0,
    verified: 0,
    pending: 0
  });

  // Close profile menu when clicking outside
  useEffect(() => {
    if (!showProfileMenu) return;
    const handleOutsideClick = (e) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(e.target)) {
        setShowProfileMenu(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('touchstart', handleOutsideClick);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('touchstart', handleOutsideClick);
    };
  }, [showProfileMenu]);

  // Get user data from localStorage
  useEffect(() => {
    const userData = localStorage.getItem('user');
    const token = localStorage.getItem('token');
    
    if (!token || !userData) {
      if (onNavigate) {
        onNavigate('login');
      }
      return;
    }
    
    try {
      const parsedUser = JSON.parse(userData);
      if (parsedUser.role !== 'teacher') {
        onNavigate('login');
        return;
      }
      setUser(parsedUser);
    } catch (error) {
      console.error('Error parsing user data:', error);
      if (onNavigate) {
        onNavigate('login');
      }
    }
  }, [onNavigate]);

  // Fetch assigned course and dashboard data
  useEffect(() => {
    const fetchTeacherData = async () => {
      if (!user) return;
      
      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        const userId = user.id || user._id || user.userId;
        
        if (!userId) {
          setError('User ID not found');
          setLoading(false);
          return;
        }
        
        // Fetch teacher's assigned courses
        const teacherResponse = await fetch(`${collegeConfig.api.baseURL}/teachers/${userId}`, {
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (!teacherResponse.ok) {
          const errorData = await teacherResponse.json();
          throw new Error(errorData.message || 'Failed to fetch teacher data');
        }
        
        const data = await teacherResponse.json();
        
        if (data.success && data.teacher && data.teacher.courses && data.teacher.courses.length > 0) {
          const course = data.teacher.courses[0];
          setAssignedCourse(course);
          
          // Fetch real statistics for this course
          await fetchCourseStats(course._id, token);
        } else {
          setAssignedCourse(null);
          setLoading(false);
        }
        
      } catch (error) {
        console.error('Error fetching teacher data:', error);
        setError(`Failed to load dashboard data: ${error.message}`);
        setLoading(false);
      }
    };

    if (user) {
      fetchTeacherData();
    }
  }, [user]);

  // Fetch all students and saved selection for My Students feature
  useEffect(() => {
    const fetchAllStudentsAndSelection = async () => {
      if (!user || !assignedCourse) return;

      try {
        setMyStudentsLoading(true);
        const token = localStorage.getItem('token');

        // Fetch all students and saved selection in parallel
        const [studentsRes, selectionRes] = await Promise.all([
          fetch(
            `${collegeConfig.api.baseURL}/students/by-course/${assignedCourse._id}`,
            {
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              }
            }
          ),
          fetch(
            `${collegeConfig.api.baseURL}/teachers/my-students/${assignedCourse._id}`,
            {
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              }
            }
          )
        ]);

        if (studentsRes.ok) {
          const studentsData = await studentsRes.json();
          const courseIdStr = assignedCourse._id?.toString();
          // Extract batch from assignedCourses into a top-level field for easy access
          const students = (studentsData.students || []).map(s => ({
            ...s,
            batch: s.assignedCourses?.find(c => c.courseId?.toString() === courseIdStr)?.batch || ''
          }));
          setAllStudents(students);

          const allIds = students.map(s => String(s._id || s.id));

          if (selectionRes.ok) {
            const selectionData = await selectionRes.json();
            if (selectionData.success && selectionData.selectedStudentIds !== null) {
              // Use saved selection, filter out IDs no longer in course
              const savedIds = selectionData.selectedStudentIds;
              const validIds = savedIds.filter(id => allIds.includes(String(id)));
              setMyStudentIds(validIds);
            } else {
              // No saved selection yet — default to all
              setMyStudentIds(allIds);
            }
          } else {
            // API error — default to all
            setMyStudentIds(allIds);
          }
        }
      } catch (error) {
        console.error('Error fetching students for My Students:', error);
      } finally {
        setMyStudentsLoading(false);
      }
    };

    fetchAllStudentsAndSelection();
  }, [user, assignedCourse]);

  // Prevent back navigation
  useEffect(() => {
    window.history.pushState(null, '', window.location.href);
    
    const handlePopState = () => {
      window.history.pushState(null, '', window.location.href);
      window.location.reload();
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  // Fetch course statistics
  const fetchCourseStats = async (courseId, token) => {
    try {
      const currentYear = new Date().getFullYear();
      
      const studentsResponse = await fetch(
        `${collegeConfig.api.baseURL}/students/by-course/${courseId}`,
        {
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (!studentsResponse.ok) {
        throw new Error('Failed to fetch enrolled students');
      }
      
      const studentsData = await studentsResponse.json();
      const totalStudents = studentsData.success && studentsData.students 
        ? studentsData.students.length 
        : 0;
      
      const logbookResponse = await fetch(
        `${collegeConfig.api.baseURL}/logbook/entries/course/${courseId}`,
        {
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      let submitted = 0;
      let verified = 0;
      let pending = 0;
      
      if (logbookResponse.ok) {
        const logbookData = await logbookResponse.json();
        
        if (logbookData.success && logbookData.entries) {
          const allEntries = logbookData.entries;
          
          const currentYearEntries = allEntries.filter(entry => {
            const entryYear = new Date(entry.createdAt).getFullYear();
            return entryYear === currentYear;
          });
          
          submitted = currentYearEntries.filter(
            e => e.status !== 'draft'
          ).length;
          
          verified = currentYearEntries.filter(
            e => e.status === 'approved'
          ).length;
          
          pending = currentYearEntries.filter(
            e => e.status === 'submitted' || e.status === 'under_review'
          ).length;
        }
      }
      
      setDashboardStats({
        totalStudents: totalStudents,
        submittedLogbooks: submitted,
        verifiedLogbooks: verified,
        pendingVerification: pending
      });
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching course stats:', error);
      setDashboardStats({
        totalStudents: 0,
        submittedLogbooks: 0,
        verifiedLogbooks: 0,
        pendingVerification: 0
      });
      await checkMonthlyVerificationStatus(courseId);
      setLoading(false);
    }
  };

  // Check monthly verification status
  useEffect(() => {
    if (assignedCourse && !loading) {
      checkMonthlyVerificationStatus(assignedCourse._id);
    }
  }, [assignedCourse, loading, user]);

  // Fetch notifications
  useEffect(() => {
    const fetchNotifications = async () => {
      if (!user) return;
      
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${collegeConfig.api.baseURL}/notices/dashboard/teachers`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (response.ok) {
          const data = await response.json();
          setNotifications(data.notices || []);
          
          const userId = user.id || user._id;
          const unread = data.notices.filter(notice => {
            if (!notice.readBy || notice.readBy.length === 0) return true;
            return !notice.readBy.some(read => 
              (read.userId === userId || read.userId?._id === userId || read.userId?.toString() === userId.toString())
            );
          }).length;
          setUnreadNotifications(unread);
        }
      } catch (error) {
        console.error('Error fetching notifications:', error);
      }
    };

    if (user) {
      fetchNotifications();
      const interval = setInterval(fetchNotifications, 30000);
      return () => clearInterval(interval);
    }
  }, [user]);

  // Handle responsive sidebar
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      if (mobile) {
        setsidebar1Open(false);
      } else {
        setsidebar1Open(true);
      }
    };

    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Animate numbers
  useEffect(() => {
    if (!loading && assignedCourse) {
      const targetStats = {
        students: dashboardStats.totalStudents,
        submitted: dashboardStats.submittedLogbooks,
        verified: dashboardStats.verifiedLogbooks,
        pending: dashboardStats.pendingVerification
      };

      const duration = 1500;
      const steps = 50;
      const stepDuration = duration / steps;

      let currentStep = 0;
      const timer = setInterval(() => {
        currentStep++;
        if (currentStep <= steps) {
          setAnimatedStats({
            students: Math.floor((targetStats.students * currentStep) / steps),
            submitted: Math.floor((targetStats.submitted * currentStep) / steps),
            verified: Math.floor((targetStats.verified * currentStep) / steps),
            pending: Math.floor((targetStats.pending * currentStep) / steps)
          });
        } else {
          clearInterval(timer);
          setAnimatedStats(targetStats);
        }
      }, stepDuration);

      return () => clearInterval(timer);
    }
  }, [dashboardStats, loading, assignedCourse]);

  useEffect(() => {
    const initPushNotifications = async () => {
      if (!user) return;
      
      try {
        const status = await checkSubscriptionStatus();
        if (!status.subscribed && status.permission !== 'denied') {
          await subscribeToPushNotifications();
        }
      } catch (error) {
        console.error('Error initializing push notifications:', error);
      }
    };

    if (user) {
      setTimeout(initPushNotifications, 2000);
    }
  }, [user]);

  // Fetch students without weekly submission
  useEffect(() => {
    if (assignedCourse && !loading) {
      fetchStudentsWithoutWeeklySubmission(assignedCourse._id);
    }
  }, [assignedCourse, loading, user]);

  // Check if teacher has verified any logbook this month
  const checkMonthlyVerificationStatus = async (courseId) => {
    if (!user || !courseId) return;
    
    try {
      const token = localStorage.getItem('token');
      
      // Get start and end of current month
      const now = new Date();
      const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
      monthStart.setHours(0, 0, 0, 0);
      
      const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      monthEnd.setHours(23, 59, 59, 999);

      // Fetch all logbook entries for this course
      const response = await fetch(
        `${collegeConfig.api.baseURL}/logbook/entries/course/${courseId}`,
        {
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        const entries = data.entries || [];
        
        const teacherId = user.id || user._id;
        
        // Check if there are any entries verified by this teacher this month
        const thisMonthVerifications = entries.filter(entry => {
          if (!entry.review || !entry.review.reviewedAt) return false;
          
          const reviewDate = new Date(entry.review.reviewedAt);
          const reviewerId = entry.review.reviewerId?._id || entry.review.reviewerId;
          
          return reviewDate >= monthStart && 
                reviewDate <= monthEnd && 
                reviewerId?.toString() === teacherId.toString();
        });

        // Find most recent verification by this teacher (any time)
        let lastVerification = null;
        const verifiedByTeacher = entries.filter(entry => {
          if (!entry.review || !entry.review.reviewedAt) return false;
          const reviewerId = entry.review.reviewerId?._id || entry.review.reviewerId;
          return reviewerId?.toString() === teacherId.toString();
        });
        
        if (verifiedByTeacher.length > 0) {
          const sortedVerifications = verifiedByTeacher.sort((a, b) => {
            const dateA = new Date(a.review.reviewedAt);
            const dateB = new Date(b.review.reviewedAt);
            return dateB - dateA; // Descending order
          });
          
          lastVerification = new Date(sortedVerifications[0].review.reviewedAt);
        }

        // Count pending logbooks
        const pending = entries.filter(
          e => e.status === 'submitted' || e.status === 'under_review'
        ).length;

        setMonthlyVerificationStatus({
          hasVerifiedThisMonth: thisMonthVerifications.length > 0,
          lastVerificationDate: lastVerification,
          pendingCount: pending,
          loading: false
        });
      } else {
        throw new Error('Failed to fetch entries');
      }
    } catch (error) {
      console.error('Error checking monthly verification status:', error);
      setMonthlyVerificationStatus({
        hasVerifiedThisMonth: false,
        lastVerificationDate: null,
        pendingCount: 0,
        loading: false
      });
    }
  };

  // Fetch students who haven't submitted logbooks this week
  const fetchStudentsWithoutWeeklySubmission = async (courseId) => {
    if (!user || !courseId) return;
    
    try {
      const token = localStorage.getItem('token');
      
      // Get start and end of current week (Monday to Sunday)
      const now = new Date();
      const dayOfWeek = now.getDay();
      const diff = now.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1);
      
      const weekStart = new Date(now.setDate(diff));
      weekStart.setHours(0, 0, 0, 0);
      
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekStart.getDate() + 6);
      weekEnd.setHours(23, 59, 59, 999);

      // Fetch all students enrolled in the course
      const studentsResponse = await fetch(
        `${collegeConfig.api.baseURL}/students/by-course/${courseId}`,
        {
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!studentsResponse.ok) {
        throw new Error('Failed to fetch students');
      }

      const studentsData = await studentsResponse.json();
      const allStudents = (studentsData.students || []).map(s => ({
        ...s,
        batch: s.assignedCourses?.find(c => c.courseId?.toString() === courseId.toString())?.batch || ''
      }));

      // Fetch all logbook entries for this course
      const logbookResponse = await fetch(
        `${collegeConfig.api.baseURL}/logbook/entries/course/${courseId}`,
        {
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!logbookResponse.ok) {
        throw new Error('Failed to fetch logbook entries');
      }

      const logbookData = await logbookResponse.json();
      const allEntries = logbookData.entries || [];

      // Find students who submitted this week
      const studentsWhoSubmittedThisWeek = new Set();
      
      allEntries.forEach(entry => {
        const submittedAt = entry.metadata?.submittedAt || entry.createdAt;
        const entryDate = new Date(submittedAt);
        
        // Check if entry is from this week and not a draft
        if (entryDate >= weekStart && 
            entryDate <= weekEnd && 
            entry.status !== 'draft') {
          const studentId = entry.studentId?._id || entry.studentId;
          studentsWhoSubmittedThisWeek.add(studentId.toString());
        }
      });

      // Filter students who haven't submitted
      const studentsWithoutSubmission = allStudents.filter(student => {
        const studentId = student._id || student.id;
        return !studentsWhoSubmittedThisWeek.has(studentId.toString());
      });

      // Sort by batch, then by name within each batch
      studentsWithoutSubmission.sort((a, b) => {
        const batchCmp = (a.batch || '').localeCompare(b.batch || '');
        if (batchCmp !== 0) return batchCmp;
        const nameA = `${a.profile?.firstName || ''} ${a.profile?.lastName || ''}`.trim().toLowerCase();
        const nameB = `${b.profile?.firstName || ''} ${b.profile?.lastName || ''}`.trim().toLowerCase();
        return nameA.localeCompare(nameB);
      });

      setStudentsWithoutSubmission({
        students: studentsWithoutSubmission,
        loading: false,
        totalStudents: allStudents.length
      });

    } catch (error) {
      console.error('Error fetching students without submission:', error);
      setStudentsWithoutSubmission({
        students: [],
        loading: false,
        totalStudents: 0
      });
    }
  };

  const sendStudentLogbookReminder = async () => {
    if (!assignedCourse || sendingStudentNotif) return;
    setSendingStudentNotif(true);
    setStudentNotifResult(null);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${collegeConfig.api.baseURL}/push/notify-students`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ courseId: assignedCourse._id })
      });
      const data = await response.json();
      setStudentNotifResult(data);
    } catch (error) {
      setStudentNotifResult({ success: false, message: 'Failed to send notification' });
    } finally {
      setSendingStudentNotif(false);
      setTimeout(() => setStudentNotifResult(null), 6000);
    }
  };

  const sendSingleStudentReminder = async (studentId) => {
    if (!assignedCourse || notifyingStudent) return;
    setNotifyingStudent(studentId);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${collegeConfig.api.baseURL}/push/notify-single-student`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ studentId, courseId: assignedCourse._id })
      });
      const data = await response.json();
      setStudentNotifResults(prev => ({ ...prev, [studentId]: data.success }));
    } catch {
      setStudentNotifResults(prev => ({ ...prev, [studentId]: false }));
    } finally {
      setNotifyingStudent(null);
      setTimeout(() => setStudentNotifResults(prev => {
        const next = { ...prev };
        delete next[studentId];
        return next;
      }), 4000);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    window.location.reload();
  };

  const handleMenuClick = (menuId) => {
    setActiveMenu(menuId);
    if (isMobile) {
      setsidebar1Open(false);
    }
  };

  // My Students handlers
  const toggleStudentSelection = (studentId) => {
    setMyStudentIds(prev =>
      prev.includes(studentId)
        ? prev.filter(id => id !== studentId)
        : [...prev, studentId]
    );
  };

  const selectAllStudents = () => {
    setMyStudentIds(allStudents.map(s => s._id || s.id));
  };

  const deselectAllStudents = () => {
    setMyStudentIds([]);
  };

  const saveMyStudents = async () => {
    if (!user || !assignedCourse) return;
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${collegeConfig.api.baseURL}/teachers/my-students/${assignedCourse._id}`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ selectedStudentIds: myStudentIds })
        }
      );

      if (response.ok) {
        setMyStudentsSaveSuccess(true);
        setTimeout(() => setMyStudentsSaveSuccess(false), 3000);
      } else {
        console.error('Failed to save student selection');
      }
    } catch (error) {
      console.error('Error saving student selection:', error);
    }
  };

  const togglesidebar1 = () => {
    setsidebar1Open(!sidebar1Open);
  };

  const markNotificationAsRead = async (notificationId) => {
    try {
      const token = localStorage.getItem('token');
      const userId = user.id || user._id;
      
      const notification = notifications.find(n => n._id === notificationId);
      const isAlreadyRead = notification?.readBy?.some(read => 
        (read.userId === userId || read.userId?._id === userId || read.userId?.toString() === userId.toString())
      );
      
      if (isAlreadyRead) return;
      
      const response = await fetch(`${collegeConfig.api.baseURL}/notices/${notificationId}/read`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        setNotifications(prev => 
          prev.map(n => n._id === notificationId 
            ? { 
                ...n, 
                readBy: [...(n.readBy || []), { userId, readAt: new Date() }] 
              }
            : n
          )
        );
        setUnreadNotifications(prev => Math.max(0, prev - 1));
      }
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const handleNotificationClick = (notification) => {
    markNotificationAsRead(notification._id);
    setSelectedNotification(notification);
    setShowNotifications(false);
  };

  const closeNotificationPopup = () => {
    setSelectedNotification(null);
  };

  const isNotificationRead = (notification) => {
    const userId = user.id || user._id;
    return notification.readBy?.some(read => 
      (read.userId === userId || read.userId?._id === userId || read.userId?.toString() === userId.toString())
    );
  };

  const [drpMode, setDrpMode] = useState('advanced');
  const [internalTestEnabled, setInternalTestEnabled] = useState(true);
  const [batchLabels, setBatchLabels] = useState({});

  useEffect(() => {
    fetch(`${collegeConfig.api.baseURL}/system-settings`)
      .then(r => r.json())
      .then(d => {
        if (d.success) {
          setDrpMode(d.drpMode || 'advanced');
          setInternalTestEnabled(d.internalTestEnabled !== false);
          if (Array.isArray(d.batchLabels)) {
            setBatchLabels(Object.fromEntries(d.batchLabels.map(b => [b.batch, b.label])));
          }
        }
      })
      .catch(() => {});
  }, []);

  const isHOD = React.useMemo(() => {
    return user?.profile?.isHOD === true;
  }, [user]);

  const sidebar1MenuItems = [
    {
      id: 'dashboard',
      title: 'Dashboard',
      icon: <FaTachometerAlt />,
      color: '#1568f6ff'
    },
    {
      id: 'view-logbooks',
      title: 'View Logbooks',
      icon: <FaBookMedical />,
      color: '#f32e2eff'
    },
    {
      id: 'checklist-grading',
      title: 'Checklist Grading',
      icon: <FaClipboardCheck />,
      color: '#E67E22'
    },
    {
      id: 'assessment-grading',
      title: 'Assessment Grading',
      icon: <FaClipboardList />,
      color: '#f2d814ff'
    },
    {
      id: 'activity-tracker',
      title: 'Activity Tracker',
      icon: <FaChartBar />,
      color: '#3498DB'
    },
    {
      id: 'my-students',
      title: 'My Students',
      icon: <FaUsers />,
      color: '#27AE60'
    },
    {
      id: 'student-history',
      title: 'Student History',
      icon: <FaHistory />,
      color: '#2E86C1'
    },
    ...(isHOD ? [
      {
        id: 'monitor-department',
        title: 'Monitor Department',
        icon: <FaBuilding />,
        color: '#8E44AD'
      },
      {
        id: 'certificates',
        title: 'Generate Certificates',
        icon: <FaAward />,
        color: '#c62828'
      },
      ...(drpMode === 'classic' ? [{
        id: 'drp-activity',
        title: 'DRP Activity',
        icon: <FaHospital />,
        color: '#E67E22'
      }] : [])
    ] : []),
    {
      id: 'dissertation',
      title: 'Thesis / Dissertation',
      icon: <FaGraduationCap />,
      color: '#5C6BC0'
    },
    {
      id: 'leave-requests',
      title: 'Leave Requests',
      icon: <FaCalendarCheck />,
      color: '#16A085'
    },
    ...(internalTestEnabled ? [{
      id: 'internal-tests',
      title: 'Internal Tests',
      icon: <FaFileAlt />,
      color: '#E67E22'
    }] : []),
    {
      id: 'additional-info',
      title: 'Additional Info',
      icon: <FaInfoCircle />,
      color: '#9B59B6'
    },
    {
      id: 'manage-account',
      title: 'Manage My Account',
      icon: <FaUserCog />,
      color: '#9B59B6'
    }
  ];

  const renderContent = () => {
    if (!loading && !assignedCourse) {
      return (
        <div className="no-course-container">
          <div className="no-course-card">
            <FaExclamationCircle className="no-course-icon" />
            <h2>No Course Assigned</h2>
            <p>Please contact your administrator to assign you to a course.</p>
            <p className="no-course-hint">Once a course is assigned, you'll be able to:</p>
            <ul className="feature-list">
              <li>View enrolled students</li>
              <li>Review and verify e-logbooks</li>
              <li>Grade students using checklists</li>
              <li>Manage student assessments</li>
              <li>Track student performance</li>
            </ul>
          </div>
        </div>
      );
    }

    switch (activeMenu) {
      case 'dashboard':
        return (
          <>
            <div className="welcome-section">
              <h1 className="welcome-title">
                Welcome back, {user?.profile?.firstName || 'Teacher'}!
                {isHOD && <span className="hod-badge"> (HOD)</span>}
              </h1>
              <p className="welcome-subtitle">
                {assignedCourse ? `Managing: ${assignedCourse.name} (${assignedCourse.code})` : 'Loading course information...'}
              </p>
            </div>

            {/* Monthly Verification Status Alert - Add this after welcome-section */}
            {!loading && assignedCourse && !monthlyVerificationStatus.loading && (
              <div style={{
                marginBottom: '30px',
                animation: 'slideInDown 0.5s ease'
              }}>
                {monthlyVerificationStatus.hasVerifiedThisMonth ? (
                  // Success - Verified logbooks this month
                  <div className="verification-alert verification-success">
                    <div className="verification-icon">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <circle cx="12" cy="12" r="10" fill="currentColor" opacity="0.2"/>
                        <path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                    <div className="verification-content">
                      <h4>✅ Great Work! Logbooks Verified This Month</h4>
                      <p>You've been actively verifying student logbooks this month. Keep up the excellent work!</p>
                      {monthlyVerificationStatus.pendingCount > 0 && (
                        <p className="pending-info">
                          <strong>{monthlyVerificationStatus.pendingCount}</strong> logbook{monthlyVerificationStatus.pendingCount !== 1 ? 's' : ''} still pending verification.
                        </p>
                      )}
                    </div>
                  </div>
                ) : (
                  // Warning - No verifications this month
                  <div className="verification-alert verification-warning">
                    <div className="verification-icon">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <circle cx="12" cy="12" r="10" fill="currentColor" opacity="0.2"/>
                        <path d="M12 8v4M12 16h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                      </svg>
                    </div>
                    <div className="verification-content">
                      <h4>⚠️ No Logbooks Verified This Month</h4>
                      <p>
                        {monthlyVerificationStatus.lastVerificationDate 
                          ? `Last verification was ${getTimeSinceVerification(monthlyVerificationStatus.lastVerificationDate)}. `
                          : 'You haven\'t verified any logbooks yet. '}
                        {monthlyVerificationStatus.pendingCount > 0 
                          ? `There are ${monthlyVerificationStatus.pendingCount} logbook${monthlyVerificationStatus.pendingCount !== 1 ? 's' : ''} waiting for your review.`
                          : 'Check back later for student submissions.'}
                      </p>
                      {monthlyVerificationStatus.pendingCount > 0 && (
                        <button 
                          onClick={() => handleMenuClick('view-logbooks')}
                          className="verify-logbook-btn"
                        >
                          View Pending Logbooks →
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {loading && (
              <div className="dashboard-loading">
                <div className="spinner"></div>
                <p>Loading dashboard statistics...</p>
              </div>
            )}

            {error && (
              <div className="alert alert-error">{error}</div>
            )}

            {/* Watch Demo */}
            {collegeConfig.demoVideos?.teacher && (
              <div style={{ textAlign: 'center', padding: '8px 20px 20px' }}>
                <DemoVideoPlayer src={collegeConfig.demoVideos.teacher} label="Watch Demo" />
              </div>
            )}

            {!loading && assignedCourse && (
              <div className="quick-actions">
                <h3 className="section-title">Quick Actions</h3>
                <div className="action-cards">
                  <div
                    className="action-card"
                    onClick={() => handleMenuClick('view-logbooks')}
                  >
                    <div className="action-icon" style={{ background: '#2ECC71' }}>
                      <FaBookMedical />
                    </div>
                    <div className="action-content">
                      <h4>View & Verify Logbooks</h4>
                      <p>Review student submissions and provide feedback</p>
                    </div>
                    <FaArrowRight className="action-arrow" />
                  </div>

                  <div
                    className="action-card"
                    onClick={() => handleMenuClick('checklist-grading')}
                  >
                    <div className="action-icon" style={{ background: '#E67E22' }}>
                      <FaClipboardCheck />
                    </div>
                    <div className="action-content">
                      <h4>Checklist Grading</h4>
                      <p>Evaluate students using observation checklists</p>
                    </div>
                    <FaArrowRight className="action-arrow" />
                  </div>

                  <div
                    className="action-card"
                    onClick={() => handleMenuClick('assessment-grading')}
                  >
                    <div className="action-icon" style={{ background: '#9B59B6' }}>
                      <FaClipboardList />
                    </div>
                    <div className="action-content">
                      <h4>Assessment Grading</h4>
                      <p>Grade student performance and overall assessments</p>
                    </div>
                    <FaArrowRight className="action-arrow" />
                  </div>

                  <div
                    className="action-card"
                    onClick={() => handleMenuClick('activity-tracker')}
                  >
                    <div className="action-icon" style={{ background: '#3498DB' }}>
                      <FaChartBar />
                    </div>
                    <div className="action-content">
                      <h4>Activity Tracker</h4>
                      <p>Consolidated view of student activities by date</p>
                    </div>
                    <FaArrowRight className="action-arrow" />
                  </div>

                  <div
                    className="action-card"
                    onClick={() => handleMenuClick('my-students')}
                  >
                    <div className="action-icon" style={{ background: '#27AE60' }}>
                      <FaUsers />
                    </div>
                    <div className="action-content">
                      <h4>My Students</h4>
                      <p>Select the students you are observing.</p>
                    </div>
                    <FaArrowRight className="action-arrow" />
                  </div>

                  {isHOD && (
                    <>
                      <div
                        className="action-card"
                        onClick={() => handleMenuClick('monitor-department')}
                      >
                        <div className="action-icon" style={{ background: '#8E44AD' }}>
                          <FaBuilding />
                        </div>
                        <div className="action-content">
                          <h4>Monitor Department</h4>
                          <p>View department reports, faculty activity & student compliance</p>
                        </div>
                        <FaArrowRight className="action-arrow" />
                      </div>
                      <div
                        className="action-card"
                        onClick={() => handleMenuClick('certificates')}
                      >
                        <div className="action-icon" style={{ background: '#c62828' }}>
                          <FaAward />
                        </div>
                        <div className="action-content">
                          <h4>Generate Certificates</h4>
                          <p>Issue course completion certificates to students batchwise</p>
                        </div>
                        <FaArrowRight className="action-arrow" />
                      </div>
                      {drpMode === 'classic' && (
                        <div
                          className="action-card"
                          onClick={() => handleMenuClick('drp-activity')}
                        >
                          <div className="action-icon" style={{ background: '#E67E22' }}>
                            <FaHospital />
                          </div>
                          <div className="action-content">
                            <h4>DRP Activity</h4>
                            <p>Monitor student Classic DRP activities and verification status</p>
                          </div>
                          <FaArrowRight className="action-arrow" />
                        </div>
                      )}
                    </>
                  )}

                  <div
                    className="action-card"
                    onClick={() => handleMenuClick('dissertation')}
                  >
                    <div className="action-icon" style={{ background: '#5C6BC0' }}>
                      <FaGraduationCap />
                    </div>
                    <div className="action-content">
                      <h4>Thesis / Dissertation</h4>
                      <p>View and grade student dissertation reports</p>
                    </div>
                    <FaArrowRight className="action-arrow" />
                  </div>

                  <div
                    className="action-card"
                    onClick={() => handleMenuClick('leave-requests')}
                  >
                    <div className="action-icon" style={{ background: '#16A085' }}>
                      <FaCalendarCheck />
                    </div>
                    <div className="action-content">
                      <h4>Leave Requests</h4>
                      <p>Review and manage student leave applications</p>
                    </div>
                    <FaArrowRight className="action-arrow" />
                  </div>

                  {internalTestEnabled && (
                    <div
                      className="action-card"
                      onClick={() => handleMenuClick('internal-tests')}
                    >
                      <div className="action-icon" style={{ background: '#E67E22' }}>
                        <FaFileAlt />
                      </div>
                      <div className="action-content">
                        <h4>Internal Tests</h4>
                        <p>Manage and evaluate internal test submissions</p>
                      </div>
                      <FaArrowRight className="action-arrow" />
                    </div>
                  )}

                  <div
                    className="action-card"
                    onClick={() => handleMenuClick('manage-account')}
                  >
                    <div className="action-icon" style={{ background: '#9B59B6' }}>
                      <FaUserCog />
                    </div>
                    <div className="action-content">
                      <h4>Manage My Account</h4>
                      <p>Update your profile and account settings</p>
                    </div>
                    <FaArrowRight className="action-arrow" />
                  </div>

                  <div
                    className="action-card"
                    onClick={handleLogout}
                  >
                    <div className="action-icon" style={{ background: '#E74C3C' }}>
                      <FaSignOutAlt />
                    </div>
                    <div className="action-content">
                      <h4>Logout</h4>
                      <p>Sign out of your account securely</p>
                    </div>
                    <FaArrowRight className="action-arrow" />
                  </div>
                </div>
              </div>
            )}

            {!loading && assignedCourse && (
              <div className="stats-grid">
                <div className="stat-card" style={{ '--card-color': '#4A90E2' }}>
                  <div className="stat-icon" style={{ background: '#4A90E2' }}>
                    <FaUserGraduate />
                  </div>
                  <div className="stat-content">
                    <h3 className="stat-value">{animatedStats.students}</h3>
                    <p className="stat-title">Enrolled Students</p>
                    <p className="stat-subtitle">In {assignedCourse.code}</p>
                  </div>
                </div>

                <div className="stat-card" style={{ '--card-color': '#2ECC71' }}>
                  <div className="stat-icon" style={{ background: '#2ECC71' }}>
                    <FaBookMedical />
                  </div>
                  <div className="stat-content">
                    <h3 className="stat-value">{animatedStats.submitted}</h3>
                    <p className="stat-title">E-Logbooks Entries</p>
                    <p className="stat-subtitle">Current year</p>
                  </div>
                </div>

                <div className="stat-card" style={{ '--card-color': '#F39C12' }}>
                  <div className="stat-icon" style={{ background: '#F39C12' }}>
                    <FaCheckCircle />
                  </div>
                  <div className="stat-content">
                    <h3 className="stat-value">{animatedStats.verified}</h3>
                    <p className="stat-title">E-Logbooks Entries Verified</p>
                    <p className="stat-subtitle">In this year</p>
                  </div>
                </div>

                <div className="stat-card" style={{ '--card-color': '#E74C3C' }}>
                  <div className="stat-icon" style={{ background: '#E74C3C' }}>
                    <FaExclamationCircle />
                  </div>
                  <div className="stat-content">
                    <h3 className="stat-value">{animatedStats.pending}</h3>
                    <p className="stat-title">Pending Verification</p>
                    <p className="stat-subtitle">Requires attention</p>
                  </div>
                </div>
              </div>
            )}
              {/* Send Logbook Reminder to Students */}
              {!loading && assignedCourse && (
                <div style={{
                  background: 'white',
                  borderRadius: '12px',
                  padding: '20px 24px',
                  marginBottom: '24px',
                  boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  flexWrap: 'wrap',
                  gap: '12px'
                }}>
                  <div>
                    <h4 style={{ margin: '0 0 4px', fontSize: '15px', fontWeight: '600', color: '#333' }}>
                      Send Logbook Reminder to Students
                    </h4>
                    <p style={{ margin: 0, fontSize: '13px', color: '#666' }}>
                      Notify all students in your course to fill their logbook entries
                    </p>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    {studentNotifResult && (
                      <span style={{
                        fontSize: '13px',
                        fontWeight: '500',
                        color: studentNotifResult.success ? '#2ECC71' : '#E74C3C'
                      }}>
                        {studentNotifResult.message}
                      </span>
                    )}
                    <button
                      onClick={sendStudentLogbookReminder}
                      disabled={sendingStudentNotif}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '10px 20px',
                        background: sendingStudentNotif ? '#bbb' : '#F39C12',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        fontSize: '14px',
                        fontWeight: '600',
                        cursor: sendingStudentNotif ? 'not-allowed' : 'pointer',
                        transition: 'background 0.2s',
                        whiteSpace: 'nowrap'
                      }}
                      onMouseEnter={e => { if (!sendingStudentNotif) e.currentTarget.style.background = '#d68910'; }}
                      onMouseLeave={e => { if (!sendingStudentNotif) e.currentTarget.style.background = '#F39C12'; }}
                    >
                      <FaPaperPlane />
                      {sendingStudentNotif ? 'Sending...' : 'Notify Students'}
                    </button>
                  </div>
                </div>
              )}

              {/* Students Without Weekly Submission - filtered by My Students */}
              {!loading && assignedCourse && (() => {
                const minBatchYear = new Date().getFullYear() - 4;
                const myFilteredStudents = studentsWithoutSubmission.students.filter(student => {
                  const studentId = String(student._id || student.id);
                  const inMyStudents = myStudentIds.length === 0 || myStudentIds.includes(studentId);
                  if (!inMyStudents) return false;
                  // Look up batch from allStudents state (reliably has batch extracted)
                  const batch = allStudents.find(s => String(s._id || s.id) === studentId)?.batch || student.batch || '';
                  if (!batch) return true; // include students with no batch set
                  const startYear = parseInt(batch.split('-')[0]);
                  return isNaN(startYear) || startYear >= minBatchYear;
                });
                const myTotalStudents = myStudentIds.length > 0
                  ? myStudentIds.length
                  : studentsWithoutSubmission.totalStudents;

                return (
                <div className="students-without-submission-section">
                  <div className="section-header">
                    <h3 className="section-title">
                      <FaExclamationCircle style={{ marginRight: '10px', color: '#F39C12' }} />
                      Students Without Weekly Logbook
                    </h3>
                    {!studentsWithoutSubmission.loading && (
                      <span className="students-count-badge">
                        {myFilteredStudents.length} / {myTotalStudents}
                      </span>
                    )}
                  </div>

                  {studentsWithoutSubmission.loading ? (
                    <div className="loading-container">
                      <div className="spinner"></div>
                      <p>Loading student data...</p>
                    </div>
                  ) : myFilteredStudents.length > 0 ? (
                    <>
                      <div className="alert-info">
                        <p>
                          <strong>{myFilteredStudents.length}</strong> student{myFilteredStudents.length !== 1 ? 's have' : ' has'} not submitted their logbook this week.
                          Consider sending them a reminder.
                        </p>
                      </div>
                      
                      <div className="students-grid">
                        {myFilteredStudents.map((student, index) => (
                          <div key={student._id || index} className="student-card-compact">
                            <div className="student-avatar-compact">
                              {(() => {
                                const firstName = student.profile?.firstName || '';
                                const lastName = student.profile?.lastName || '';
                                const initials = `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
                                return initials || '?';
                              })()}
                            </div>
                            <div className="student-info-compact">
                              <h4 className="student-name-compact">
                                {student.profile?.firstName || ''} {student.profile?.lastName || ''}
                              </h4>
                              <p className="student-email-compact">
                                {student.email || 'No email'}
                              </p>
                              {(() => {
                                const batch = allStudents.find(s => String(s._id || s.id) === String(student._id || student.id))?.batch || student.batch || '';
                                return batch ? (
                                  <span style={{
                                    display: 'inline-block', marginTop: 3,
                                    fontSize: 11, fontWeight: 600, padding: '1px 8px',
                                    borderRadius: 10, background: '#ede9ff', color: '#5b4fcf'
                                  }}>{batchLabels[batch] || batch}</span>
                                ) : null;
                              })()}
                            </div>
                            <div className="student-status-compact" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '6px' }}>
                              <span className="status-badge-missing">Not Submitted</span>
                              <button
                                onClick={() => sendSingleStudentReminder(student._id || student.id)}
                                disabled={!!notifyingStudent}
                                title="Send logbook reminder to this student"
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '5px',
                                  padding: '4px 10px',
                                  fontSize: '11px',
                                  fontWeight: '600',
                                  border: 'none',
                                  borderRadius: '6px',
                                  cursor: notifyingStudent ? 'not-allowed' : 'pointer',
                                  background: studentNotifResults[student._id || student.id] === true
                                    ? '#2ECC71'
                                    : studentNotifResults[student._id || student.id] === false
                                    ? '#E74C3C'
                                    : '#F39C12',
                                  color: 'white',
                                  opacity: notifyingStudent && notifyingStudent !== (student._id || student.id) ? 0.5 : 1,
                                  whiteSpace: 'nowrap'
                                }}
                              >
                                <FaPaperPlane style={{ fontSize: '9px' }} />
                                {notifyingStudent === (student._id || student.id)
                                  ? 'Sending...'
                                  : studentNotifResults[student._id || student.id] === true
                                  ? 'Sent!'
                                  : studentNotifResults[student._id || student.id] === false
                                  ? 'Failed'
                                  : 'Remind'}
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  ) : (
                    <div className="all-submitted-message">
                      <div className="success-icon-large">
                        <svg width="60" height="60" viewBox="0 0 24 24" fill="none">
                          <circle cx="12" cy="12" r="10" fill="#2ECC71" opacity="0.2"/>
                          <path d="M9 12l2 2 4-4" stroke="#2ECC71" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </div>
                      <h4>All Students Have Submitted</h4>
                      <p>Every student in {assignedCourse.code} has submitted their logbook this week.</p>
                    </div>
                  )}
                </div>
                );
              })()}
          </>
        );
        
      
      case 'activity-tracker':
        return (
          <ActivityTracker
            user={user}
            assignedCourse={assignedCourse}
            myStudentIds={myStudentIds}
            allStudents={allStudents}
          />
        );

      case 'monitor-department':
        return (
          <MonitorDepartment
            user={user}
            assignedCourse={assignedCourse}
          />
        );

      case 'certificates':
        return (
          <CertificateGeneration />
        );

      case 'drp-activity':
        return (
          <ClassicDRPMonitor user={user} />
        );

      case 'my-students':
        return (
          <MyStudents
            user={user}
            assignedCourse={assignedCourse}
            allStudents={allStudents}
            myStudentIds={myStudentIds}
            setMyStudentIds={setMyStudentIds}
            batchLabels={batchLabels}
          />
        );

      case 'student-history':
        return (
          <StudentAssignmentHistory />
        );

      case 'view-logbooks':
        return (
          <ViewLogbooks
            user={user}
            assignedCourse={assignedCourse}
            myStudentIds={myStudentIds}
            allStudents={allStudents}
            batchLabels={batchLabels}
          />
        );

      case 'checklist-grading':
        return (
          <ChecklistGrading
            user={user}
            assignedCourse={assignedCourse}
            myStudentIds={myStudentIds}
          />
        );

      case 'assessment-grading':
        return (
          <AssessmentGrading
            user={user}
            assignedCourse={assignedCourse}
            myStudentIds={myStudentIds}
          />
        );
      
      case 'dissertation':
        return (
          <TeacherDissertation
            user={user}
            assignedCourse={assignedCourse}
            allStudents={allStudents}
            myStudentIds={myStudentIds}
          />
        );

      case 'leave-requests':
        return (
          <LeaveRequests
            user={user}
            assignedCourse={assignedCourse}
          />
        );

      case 'internal-tests':
        return (
          <InternalTests
            user={user}
            assignedCourse={assignedCourse}
            allStudents={allStudents}
          />
        );

      case 'additional-info':
        return (
          <AdditionalInfo
            user={user}
            assignedCourse={assignedCourse}
          />
        );

      case 'manage-account':
        return (
          <ManageTeacherAccount user={user} />
        );
      
      default:
        return null;
    }
  };

  if (!user) {
    return null;
  }

  // Helper function to format time since last verification
  const getTimeSinceVerification = (lastDate) => {
    const now = new Date();
    const diffTime = Math.abs(now - lastDate);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'today';
    if (diffDays === 1) return 'yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 14) return 'last week';
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    if (diffDays < 60) return 'last month';
    return `${Math.floor(diffDays / 30)} months ago`;
  };

  return (
    <div className="teacher-dashboard-container">
      <aside className={`sidebar1 ${sidebar1Open ? 'open' : 'closed'}`}>
        <div className="sidebar1-header">
          <div className="logo-container">
            <img 
              src={collegeConfig.logo} 
              alt={`${collegeConfig.shortName} Logo`} 
              className="sidebar1-logo" 
            />
            <div className="logo-text">
              <h2>{collegeConfig.shortName}</h2>
              <p>{collegeConfig.appName}</p>
            </div>
          </div>
        </div>

        <nav className="sidebar1-nav">
          {sidebar1MenuItems.map((item) => (
            <div
              key={item.id}
              className={`sidebar1-item ${activeMenu === item.id ? 'active' : ''}`}
              onClick={() => handleMenuClick(item.id)}
            >
              <span className="sidebar1-icon" style={{ color: item.color }}>
                {item.icon}
              </span>
              <span className="sidebar1-text">{item.title}</span>
            </div>
          ))}
        </nav>

        <div className="sidebar1-footer">
          <div className="sidebar1-item logout-item" onClick={handleLogout}>
            <span className="sidebar1-icon">
              <FaSignOutAlt />
            </span>
            <span className="sidebar1-text">Logout</span>
          </div>
        </div>
      </aside>

      <div className={`main-content ${sidebar1Open ? 'sidebar1-open' : 'sidebar1-closed'}`}>
        <header className="teacher-header">
          <div className="header-left">
            <button className="sidebar1-toggle" onClick={togglesidebar1}>
              {sidebar1Open ? <FaTimes /> : <FaBars />}
            </button>
            <h2 className="page-title">
              {sidebar1MenuItems.find(item => item.id === activeMenu)?.title || 'Dashboard'}
            </h2>
          </div>

          <div className="header-right">
            {programSwitcher}
            <div className="notification-wrapper" style={{ position: 'relative', marginRight: '20px' }}>
              <div 
                className="notification-icon"
                onClick={() => setShowNotifications(!showNotifications)}
                style={{
                  cursor: 'pointer',
                  fontSize: '20px',
                  color: '#666',
                  position: 'relative'
                }}
              >
                <FaBell />
                {unreadNotifications > 0 && (
                  <span style={{
                    position: 'absolute',
                    top: '-8px',
                    right: '-8px',
                    background: '#E74C3C',
                    color: 'white',
                    borderRadius: '50%',
                    width: '20px',
                    height: '20px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '12px',
                    fontWeight: 'bold'
                  }}>
                    {unreadNotifications}
                  </span>
                )}
              </div>
              
              {showNotifications && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  right: 0,
                  background: 'white',
                  borderRadius: '12px',
                  boxShadow: '0 10px 40px rgba(0, 0, 0, 0.15)',
                  minWidth: '350px',
                  maxWidth: 'calc(100vw - 40px)',
                  maxHeight: '400px',
                  overflow: 'hidden',
                  zIndex: 1000,
                  marginTop: '10px'
                }}>
                  <div style={{
                    padding: '15px 20px',
                    borderBottom: '1px solid #e9ecef',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <h3 style={{ margin: 0, fontSize: '16px' }}>Notifications</h3>
                    {unreadNotifications > 0 && (
                      <span style={{ fontSize: '12px', color: '#666' }}>
                        {unreadNotifications} unread
                      </span>
                    )}
                  </div>
                  <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                    {notifications.length > 0 ? (
                      notifications.slice(0, 5).map((notification) => (
                        <div 
                          key={notification._id}
                          style={{
                            padding: '15px 20px',
                            borderBottom: '1px solid #f0f0f0',
                            cursor: 'pointer',
                            background: !isNotificationRead(notification) ? '#f8f9fa' : 'white',
                            transition: 'background 0.2s'
                          }}
                          onClick={() => handleNotificationClick(notification)}
                          onMouseEnter={(e) => e.currentTarget.style.background = '#f8f9fa'}
                          onMouseLeave={(e) => e.currentTarget.style.background = 
                            !isNotificationRead(notification) ? '#f8f9fa' : 'white'
                          }
                        >
                          <h4 style={{ margin: '0 0 5px', fontSize: '14px', fontWeight: '600' }}>
                            {notification.title}
                          </h4>
                          <p style={{ margin: '0 0 5px', fontSize: '13px', color: '#666' }}>
                            {notification.content.substring(0, 100)}...
                          </p>
                          <span style={{ fontSize: '11px', color: '#999' }}>
                            {new Date(notification.createdAt).toLocaleDateString()}
                          </span>
                        </div>
                      ))
                    ) : (
                      <div style={{ padding: '30px', textAlign: 'center', color: '#999' }}>
                        <p>No notifications</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="profile-section" ref={profileMenuRef} onClick={() => setShowProfileMenu(!showProfileMenu)}>
              <div className="profile-avatar1">
                <FaUser />
              </div>
              <div className="profile-info">
                <span className="profile-name">
                  {user?.profile?.firstName} {user?.profile?.lastName}
                </span>
                <span className="profile-role">Teacher{isHOD && ' (HOD)'}</span>
              </div>
              <span className="dropdown-arrow">▼</span>
              
              {showProfileMenu && (
                <div className="profile-dropdown">
                  <div className="dropdown-item" onClick={() => handleMenuClick('manage-account')}>
                    <FaUserCog /> My Account
                  </div>
                  <div className="dropdown-divider"></div>
                  <div className="dropdown-item logout" onClick={handleLogout}>
                    <FaSignOutAlt /> Logout
                  </div>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Notification Modal */}
        {selectedNotification && (
          <div 
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.6)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 2000,
              padding: '20px'
            }}
            onClick={closeNotificationPopup}
          >
            <div 
              onClick={(e) => e.stopPropagation()}
              style={{
                background: 'white',
                borderRadius: '16px',
                padding: '30px',
                maxWidth: '600px',
                width: '100%',
                maxHeight: '80vh',
                overflow: 'auto',
                boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
                position: 'relative',
                animation: 'modalSlideIn 0.3s ease'
              }}
            >
              <button
                onClick={closeNotificationPopup}
                style={{
                  position: 'absolute',
                  top: '20px',
                  right: '20px',
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#666',
                  padding: '5px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'color 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.color = '#E74C3C'}
                onMouseLeave={(e) => e.currentTarget.style.color = '#666'}
              >
                <FaTimes />
              </button>

              <div style={{ marginBottom: '15px' }}>
                <span style={{
                  display: 'inline-block',
                  padding: '4px 12px',
                  background: selectedNotification.priority === 'high' ? '#E74C3C' : 
                             selectedNotification.priority === 'medium' ? '#F39C12' : '#3498DB',
                  color: 'white',
                  borderRadius: '12px',
                  fontSize: '12px',
                  fontWeight: '600',
                  textTransform: 'uppercase'
                }}>
                  {selectedNotification.priority} Priority
                </span>
                <span style={{
                  display: 'inline-block',
                  marginLeft: '10px',
                  padding: '4px 12px',
                  background: '#9B59B6',
                  color: 'white',
                  borderRadius: '12px',
                  fontSize: '12px',
                  fontWeight: '600',
                  textTransform: 'capitalize'
                }}>
                  {selectedNotification.type}
                </span>
              </div>

              <h2 style={{
                margin: '0 0 15px',
                fontSize: '24px',
                color: '#333',
                paddingRight: '40px'
              }}>
                {selectedNotification.title}
              </h2>

              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '15px',
                marginBottom: '20px',
                paddingBottom: '20px',
                borderBottom: '1px solid #e9ecef'
              }}>
                <span style={{ fontSize: '13px', color: '#666' }}>
                  Posted: {new Date(selectedNotification.publishDate || selectedNotification.createdAt).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </span>
                {selectedNotification.expiryDate && (
                  <span style={{ fontSize: '13px', color: '#E74C3C' }}>
                    Expires: {new Date(selectedNotification.expiryDate).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </span>
                )}
              </div>

              <div style={{
                fontSize: '15px',
                lineHeight: '1.8',
                color: '#555',
                whiteSpace: 'pre-wrap'
              }}>
                {selectedNotification.content}
              </div>

              {selectedNotification.attachments && selectedNotification.attachments.length > 0 && (
                <div style={{ marginTop: '25px' }}>
                  <h4 style={{ margin: '0 0 10px', fontSize: '16px', color: '#333' }}>
                    Attachments
                  </h4>
                  {selectedNotification.attachments.map((attachment, index) => (
                    <a
                      key={index}
                      href={attachment.fileUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        display: 'block',
                        padding: '10px 15px',
                        background: '#f8f9fa',
                        borderRadius: '8px',
                        marginBottom: '8px',
                        color: '#3498DB',
                        textDecoration: 'none',
                        fontSize: '14px',
                        transition: 'background 0.2s'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.background = '#e9ecef'}
                      onMouseLeave={(e) => e.currentTarget.style.background = '#f8f9fa'}
                    >
                      📎 {attachment.fileName}
                    </a>
                  ))}
                </div>
              )}

              <div style={{
                marginTop: '25px',
                paddingTop: '20px',
                borderTop: '1px solid #e9ecef',
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center'
              }}>
                <button
                  onClick={closeNotificationPopup}
                  style={{
                    padding: '10px 24px',
                    background: '#667eea',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'background 0.2s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = '#5568d3'}
                  onMouseLeave={(e) => e.currentTarget.style.background = '#667eea'}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="page-content">
          {renderContent()}
        </div>

        <footer className="teacher-footer">
          <AppDownloadBanner />
          <p>© {collegeConfig.copyrightYear} {collegeConfig.collegeName} | Teacher Dashboard v{collegeConfig.version}</p>
        </footer>
      </div>

      {isMobile && sidebar1Open && (
        <div className="sidebar1-overlay" onClick={() => setsidebar1Open(false)}></div>
      )}

      <style jsx>{`
        @keyframes modalSlideIn {
          from {
            opacity: 0;
            transform: translateY(-20px) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }

        @media (max-width: 768px) {
          .notification-wrapper > div:nth-child(2) {
            position: fixed !important;
            top: 70px !important;
            left: 20px !important;
            right: 20px !important;
            min-width: auto !important;
            max-width: none !important;
            width: calc(100vw - 40px) !important;
            max-height: calc(100vh - 100px) !important;
          }
        }

        @media (max-width: 480px) {
          .notification-wrapper > div:nth-child(2) {
            left: 10px !important;
            right: 10px !important;
            width: calc(100vw - 20px) !important;
          }
        }
      `}</style>
    </div>
  );
};

export default TeacherDashboard;